import { threadsApi } from '@/api/threads.api';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { MOTION } from '@/components/common/motion';
import { DROP_ZONE, DROP_ZONE_ACTIVE } from '@/components/common/surfaces';
import type { ThreadTableImportReport } from '@/types/thread.types';
import { extractDroppedFiles } from '@/utils/dropFiles';
import { UploadSimple } from '@phosphor-icons/react';
import type { FC } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

/** The only suffix the backend accepts for the thread table. */
const ACCEPTED_EXTENSION = '.csv';

/** Why one upload failed, so the component can pick the message without re-reading axios internals. */
type ImportFailure =
  | { kind: 'invalidFile'; detail: string }
  | { kind: 'unauthorized' }
  | { kind: 'server' }
  | { kind: 'network' }
  | { kind: 'timeout' }
  | { kind: 'unknown'; detail?: string };

/** Network-layer codes that mean "the request never reached a usable backend". */
const NETWORK_ERROR_CODES = ['ECONNREFUSED', 'ENOTFOUND', 'ERR_NETWORK'];

const isCsvFile = (file: File): boolean => file.name.toLowerCase().endsWith(ACCEPTED_EXTENSION);

/**
 * Sort an upload rejection into one of the cases the UI has words for.
 *
 * Two shapes arrive here: an `AxiosError` carrying the backend `detail`, and the plain
 * `Error` the response interceptor in `api/client.ts` substitutes for timeouts. The backend
 * answers 400 for a wrong suffix, malformed CSV and bad encoding alike, and its `detail`
 * says which — so 400 is surfaced verbatim rather than flattened into a generic message.
 */
function classifyImportError(error: unknown): ImportFailure {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as {
      response?: { status?: number; data?: { detail?: unknown } };
      code?: string;
    };
    const status = axiosError.response?.status;
    // The backend's own 4xx answers carry `detail` as a sentence, but FastAPI's automatic 422
    // makes it an array of validation objects — only a string is safe to show to the user.
    const rawDetail = axiosError.response?.data?.detail;
    const detail = typeof rawDetail === 'string' ? rawDetail : undefined;

    if (axiosError.code === 'ECONNABORTED') return { kind: 'timeout' };
    if (axiosError.code && NETWORK_ERROR_CODES.includes(axiosError.code)) return { kind: 'network' };
    // 400 is a rejected file (wrong suffix, malformed CSV, bad encoding); 422 is a malformed
    // multipart request. Both mean "this upload was not usable", so they share a message.
    if (status === 400 || status === 422) return { kind: 'invalidFile', detail: detail ?? '' };
    if (status === 401 || status === 403) return { kind: 'unauthorized' };
    if (status != null && status >= 500) return { kind: 'server' };
    if (detail) return { kind: 'unknown', detail };
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    if (message.includes('timeout') || message.includes('took too long')) return { kind: 'timeout' };
    if (message.includes('network') || message.includes('connection')) return { kind: 'network' };
  }

  return { kind: 'unknown' };
}

interface ThreadTableDropZoneProps {
  /** Called with the backend's report once an upload finished, so the parent can refetch and summarise. */
  onImported: (report: ThreadTableImportReport) => void | Promise<void>;
  className?: string;
}

/**
 * Drop target that fills the thread table from the Kasko CSV export.
 *
 * Takes exactly one `.csv` file — by drop or by file picker — posts it to
 * `POST /thread-specs/load-csv` and hands the parent the report it answered with. Every
 * failure message stays here; the parent only ever hears about uploads that worked, and it
 * owns the success toast because only it can say what the import changed in the table.
 *
 * The upload is an upsert keyed on the thread designation, so re-dropping a corrected file
 * is a safe, repeatable operation rather than something that needs a delete-all first.
 */
export const ThreadTableDropZone: FC<ThreadTableDropZoneProps> = ({ onImported, className = '' }) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const failureMessage = (failure: ImportFailure): string => {
    switch (failure.kind) {
      case 'invalidFile':
        return failure.detail ? t('threads.importInvalidFile', { detail: failure.detail }) : t('threads.importFailed');
      case 'unauthorized':
        return t('threads.importUnauthorized');
      case 'server':
        return t('threads.importServerError');
      case 'network':
        return t('threads.importNetworkError');
      case 'timeout':
        return t('threads.importTimeout');
      default:
        return failure.detail || t('threads.importFailed');
    }
  };

  const runImport = async (file: File) => {
    setIsImporting(true);
    try {
      const report = await threadsApi.importCsv(file);
      await onImported(report);
    } catch (error) {
      console.error('Thread table import failed:', error);
      toast.error(failureMessage(classifyImportError(error)));
    } finally {
      setIsImporting(false);
      // Let the same file be picked again after a failed run.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  /** Accept a candidate set from either input path; returns the single file to upload. */
  const pickSingleCsv = (files: File[]): File | null => {
    const csvFiles = files.filter(isCsvFile);
    if (csvFiles.length === 0) {
      toast.error(t('validation.threadTableFileRequired'));
      return null;
    }
    if (csvFiles.length > 1) {
      toast.error(t('validation.threadTableSingleFile'));
      return null;
    }
    return csvFiles[0];
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isImporting) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (isImporting) return;

    const { files, hadDirectory, receivedAnything } = extractDroppedFiles(e.dataTransfer);
    if (files.length === 0) {
      if (hadDirectory) {
        toast.error(t('validation.dropFolderThreadTable'));
      } else if (!receivedAnything) {
        toast.error(t('validation.dropNoFileReceived'));
      } else {
        toast.error(t('validation.threadTableFileRequired'));
      }
      return;
    }

    const file = pickSingleCsv(files);
    if (file) await runImport(file);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected || selected.length === 0) return;

    const file = pickSingleCsv(Array.from(selected));
    if (file) {
      await runImport(file);
      return;
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {/* Nested radius: the dialog content has no card of its own, so the drop area is the outer
          box here and takes the 12 px step below a 16 px card. */}
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={isImporting}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`flex w-full flex-col items-center justify-center gap-2 px-6 py-10 text-center ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 disabled:cursor-progress ${
          isDragging ? DROP_ZONE_ACTIVE : DROP_ZONE
        }`}
      >
        {isImporting ? (
          <LoadingSpinner size="small" />
        ) : (
          <UploadSimple size={32} className={isDragging ? 'text-primary' : 'text-gray-400'} aria-hidden="true" />
        )}
        <span className="block text-sm font-medium text-gray-900">
          {isImporting ? t('threads.importInProgress') : t('threads.importDropZoneTitle')}
        </span>
        <span className="block text-xs text-gray-500">
          {isImporting ? t('threads.importInProgressHint') : t('threads.importDropZoneHint')}
        </span>
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        className="hidden"
        onChange={handleFileSelect}
        disabled={isImporting}
      />
    </div>
  );
};
